import React, { useRef, useEffect } from 'react';
import IntlTelInput from 'intl-tel-input/react';
import 'intl-tel-input/styles';
import styled from '@emotion/styled';
import { debounce, type DebouncedFunc } from 'lodash';

const StyledWrapper = styled.div<{ location: 'profile' | 'self-registration' }>`
  .iti {
    width: 100%;
  }

  .iti input {
    width: 100%;
    padding: 4px 11px;
    font-size: 14px;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    transition: all 0.2s;

    &:focus {
      box-shadow: 0 0 0 2px rgba(5, 145, 255, 0.1);
      outline: 0;
    }

    &:disabled {
      color: ${({ location }) => (location === 'profile' ? '#000 !important' : 'rgba(0, 0, 0, 0.25)')};
      background-color: rgba(0, 0, 0, 0.04);
      cursor: not-allowed;
    }
  }
`;

interface MobileInputContainerProps {
  mobile: string;
  setMobile: (val: string) => void;
  setValidMobile?: null | ((val: boolean) => void);
  autoComplete?: boolean;
  disabled?: boolean;
  inputId: string;
  location: 'profile' | 'self-registration';
  separateTelCode: boolean;
  onBlur?: () => void;
}

const loadUtilsScript = async () => {
  try {
    return await import('intl-tel-input/utils');
  } catch (error) {
    console.error('Failed to load intl-tel-input utils:', error);
    return null;
  }
};

const MobileInput = ({
  mobile,
  setMobile,
  setValidMobile = null,
  autoComplete = true,
  disabled = false,
  inputId = '',
  location = 'self-registration',
  separateTelCode = true,
  onBlur
}: MobileInputContainerProps) => {
  const itiRef = useRef<any>(null);
  const isFormattingRef = useRef(false);
  const prevValidityRef = useRef<boolean | null>(null);
  const prevMobileRef = useRef<string>('');
  const debouncedFormatNumberRef = useRef<DebouncedFunc<(number: string) => void>>(null);

  useEffect(() => {
    debouncedFormatNumberRef.current = debounce((number: string) => {
      // Don't format if the number hasn't changed
      if (prevMobileRef.current === number) {
        return;
      }

      if (itiRef.current && number) {
        try {
          const instance = itiRef.current.getInstance();

          if (instance && typeof instance.setNumber === 'function') {
            isFormattingRef.current = true;
            prevMobileRef.current = number;
            instance.setNumber(number);
            isFormattingRef.current = false;
          }
        } catch (error) {
          console.error('Error formatting phone number:', error);
          isFormattingRef.current = false;
        }
      }
    }, 500);

    return () => {
      debouncedFormatNumberRef.current?.cancel?.();
    };
  }, []);

  const handleChangeNumber = (number: string) => {
    if (isFormattingRef.current) {
      setMobile(number);
      return;
    }

    if (!number || number.trim() === '') {
      prevMobileRef.current = '';
    }

    setMobile(number);
    debouncedFormatNumberRef.current?.(number);
  };

  const handleChangeValidity = (valid: boolean) => {
    // Only call setValidMobile if validity actually changed
    if (setValidMobile && prevValidityRef.current !== valid) {
      prevValidityRef.current = valid;
      setValidMobile(valid);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    const cleanedText = text.replace(/[^\d+\s()-]/g, '');

    if (text === cleanedText) return;

    e.preventDefault();
    const input = e.currentTarget;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;

    const newValue = input.value.slice(0, start) + cleanedText + input.value.slice(end);
    handleChangeNumber(newValue);

    const newCursorPos = start + cleanedText.length;
    input.setSelectionRange(newCursorPos, newCursorPos);
  };

  const formattedMobile = mobile && !mobile.startsWith('+') ? `+${mobile}` : mobile;

  return (
    <StyledWrapper location={location} onBlur={onBlur}>
      <IntlTelInput
        ref={itiRef}
        initialValue={formattedMobile}
        onChangeNumber={handleChangeNumber}
        onChangeValidity={handleChangeValidity}
        initOptions={{
          autoPlaceholder: 'off',
          nationalMode: true,
          initialCountry: mobile ? undefined : 'gb',
          formatOnDisplay: true,
          formatAsYouType: true,
          separateDialCode: separateTelCode,
          // @ts-expect-error
          loadUtils: loadUtilsScript,
          strictMode: true
        }}
        disabled={disabled}
        inputProps={{
          autoComplete: autoComplete ? 'tel' : undefined,
          className: 'ant-input ant-input-outlined css-var-«r0» ant-input-css-var',
          id: inputId,
          'data-testid': inputId,
          name: inputId,
          onPaste: handlePaste
        }}
      />
    </StyledWrapper>
  );
};

export default MobileInput;
